import { Component, inject, signal } from '@angular/core';
import { StudentService } from '../../../../../services/student';
import {
  IStudentEnrollment,
  IStudentEnrollments,
  IStudent,
  IStudents,
} from '../../../../../interfaces/student';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { FlashService } from '../../../../../services/flash';
import { ActivatedRoute, Router } from '@angular/router';
import { ClassService } from '../../../../../services/class';
import { IClass, ITeacherClassSubjects } from '../../../../../interfaces/class';
import { ShimmerEffect } from '../../../../../shared/shimmer-effect/shimmer-effect';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { Helpers } from '../../../../../utilities/helpers';
import { NgClass } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { AcademicYearService } from '../../../../../services/academic-year';
import { IAcademicYear, IAcademicYears } from '../../../../../interfaces/academic-year';
import { Session } from '../../../../../services/session';
import { DashboardService } from '../../../../../services/dashboard';
import { SubjectService } from '../../../../../services/subject';
import { TeacherService } from '../../../../../services/teacher';
import { ITeacher, ITeachers } from '../../../../../interfaces/teacher';
import { TeacherClassSubjectService } from '../../../../../services/teacher-class-subject';
import { ILevelSubject, ILevelSubjects } from '../../../../../interfaces/subject';
import { AcademicReportService } from '../../../../../services/academic-report';
import { AcademicTermService } from '../../../../../services/academic-term';
import { IAcademicTerms } from '../../../../../interfaces/academic-term';
import { IStudentAcademicReport } from '../../../../../interfaces/academic-report';

@Component({
  selector: 'app-class-detail',
  imports: [
    ShimmerEffect,
    ActionButton,
    LoadingIndicator,
    NotFoundError,
    Pagination,
    NgClass,
    FormsModule,
  ],
  templateUrl: './class-detail.html',
  styleUrl: './class-detail.css',
})
export class ClassDetail {
  constructor(
    private studentService: StudentService,
    private route: ActivatedRoute,
    private router: Router,
    private classService: ClassService,
    private academicYearService: AcademicYearService,
    private session: Session,
    private dashboardService: DashboardService,
    protected subjectService: SubjectService,
    protected teacherService: TeacherService,
    private teacherClassSubjectService: TeacherClassSubjectService,
    private academicReportService: AcademicReportService,
    private academicTermService: AcademicTermService,
  ) {
    this.route.queryParamMap.subscribe((params) => {
      this.classId = params.get('id')!;
    });

    if (!this.classId) {
      void this.router.navigate(['/dashboard/class']);
    }

    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.loggedUserId = this.session.decodeToken(token)?.sub;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    this.getClass();
    this.getDashboardData();
    this.getClassStudents();
    this.getAcademicYears();
    this.getTeacherClassSubjects('', '', this.classId);
  }

  protected classStudents = signal<IStudentEnrollments | null>(null);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected isFiltering = signal<boolean>(false);
  protected pagination = signal({ page: 0, size: 10 });
  protected readonly flashService = inject(FlashService);
  protected classId!: string;
  protected class = signal<IClass | null>(null);
  protected isLoading = signal<boolean>(false);
  protected showAddStudent = signal<boolean>(false);
  protected readonly Helpers = Helpers;
  protected changeStatus = signal<boolean>(false);
  protected students = signal<IStudents | null>(null);
  protected query!: string;
  protected studentPublicId!: string;
  protected studentName: string = '';
  protected studentId!: string;
  protected academicYears = signal<IAcademicYears | null>(null);
  protected academicYear = signal<IAcademicYear | null>(null);
  protected academicYearId: string = '';
  protected startDate = '';
  protected endDate = '';
  protected enrollmentDate: string = '';
  protected schoolId?: string;
  protected isSubmitting = signal<boolean>(false);
  protected loggedUserRole?: string;
  protected loggedUserId?: string;
  protected dashboardData = signal<any>(null);
  protected malePercentage = signal<number>(0);
  protected filterAcademicYear: string = '';
  protected searchStudent: string = '';
  protected validStatuses: string[] = ['ACTIVE', 'COMPLETED', 'TRANSFERRED', 'CANCELLED'];
  protected newStatus: string = '';
  protected currentStatus!: string;
  protected selectedStudent!: IStudentEnrollment['payload'];
  protected isEditing = signal<boolean>(false);
  protected editEnrollment = signal<boolean>(false);
  protected currentEnrollment: boolean = false;
  protected admins = ['SUPER_ADMIN', 'SCHOOL_ADMIN'];
  protected teacherClassSubjects = signal<ITeacherClassSubjects | null>(null);
  protected invalidStatuses = [
    'INACTIVE',
    'COMPLETED',
    'ARCHIVED',
    'DEACTIVATED',
    'SUSPENDED',
    'GRADUATED',
    'TRANSFERRED',
    'WITHDRAWN',
  ];
  protected assignClassTeacher = signal<boolean>(false);
  protected teachers = signal<ITeachers | null>(null);
  protected classTeacherId: string = '';
  protected selectedClassId!: string;
  protected teacherName: string = '';
  protected teacherPublicId!: string;
  protected assignSubjectTeacherCheck = signal<boolean>(false);
  protected isAssigningSubjectTeacher = signal<boolean>(false);
  protected assignmentRole: string = '';
  protected selectedLevelSubject!: ILevelSubject['payload'] | null;
  protected levelSubjects = signal<ILevelSubjects | null>(null);
  protected showReportCard = signal<boolean>(false);
  protected reportCardStudent = signal<IStudentEnrollment['payload'] | null>(null);
  protected reportCardTerms = signal<IAcademicTerms | null>(null);
  protected reportCardTermId = '';
  protected reportCardAcademicYearId = '';
  protected academicReport = signal<IStudentAcademicReport | null>(null);
  protected isLoadingReport = signal<boolean>(false);

  protected getClassStudents(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isFiltering.set(true);

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
      class_id: this.classId,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.studentService
      .getStudentEnrollments(`${ApiEndpoints.url}/student-enrollment`, params)
      .subscribe({
        next: (data) => {
          this.classStudents.set(data);
          this.isFiltering.set(false);

          if (filterType === 'academic_year_id') {
            this.getTeacherClassSubjects(filterType, value, this.classId);
          }
        },
        error: (err) => {
          this.isFiltering.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load students. Refresh the page',
          );
        },
      });
  }

  protected getClass(): void {
    this.isLoading.set(true);

    this.classService.getClassById(`${ApiEndpoints.url}/class/${this.classId}`).subscribe({
      next: (res) => {
        this.class.set(res);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load classes. Refresh the page',
        );
      },
    });
  }

  protected getTeacherClassSubjects(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
    classId: string,
  ): void {
    this.isFiltering.set(true);

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
      class_id: classId,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.subjectService
      .getTeacherClassSubjects(`${ApiEndpoints.url}/teacher-class-subject`, params)
      .subscribe({
        next: (res) => {
          this.teacherClassSubjects.set(res);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load subjects for teachers. Refresh the page',
          );
        },
      });
  }

  protected openReportCard(student: IStudentEnrollment['payload']): void {
    this.reportCardStudent.set(student);
    this.reportCardAcademicYearId = student.academic_year_id;
    this.reportCardTermId = '';
    this.reportCardTerms.set(null);
    this.academicReport.set(null);
    this.isLoadingReport.set(true);
    this.showReportCard.set(true);
    this.getReportCardTerms();
  }

  protected getReportCardTerms(): void {
    if (!this.reportCardAcademicYearId) {
      this.getStudentReport();
      return;
    }

    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
      academic_year_id: this.reportCardAcademicYearId,
    };

    this.academicTermService
      .getAcademicTerms(`${ApiEndpoints.url}/academic-term`, params)
      .subscribe({
        next: (res) => {
          this.reportCardTerms.set(res);

          const currentTerm = res.payload.find((term) => term.current_term);
          this.reportCardTermId = currentTerm?.id ?? '';

          this.getStudentReport();
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load academic terms. Refresh the page.',
          );
          this.getStudentReport();
        },
      });
  }

  protected getStudentReport(): void {
    const student = this.reportCardStudent();

    if (!student) return;

    const params: Record<string, string | number> = {
      academic_year_id: this.reportCardAcademicYearId,
    };

    if (this.reportCardTermId) {
      params['term_id'] = this.reportCardTermId;
    }

    this.isLoadingReport.set(true);

    this.academicReportService
      .getStudentAcademicReport(
        `${ApiEndpoints.url}/academic-report/student/${student.student_id}`,
        params,
      )
      .subscribe({
        next: (res) => {
          this.academicReport.set(res);
          this.isLoadingReport.set(false);
        },
        error: (err) => {
          this.isLoadingReport.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load the report card. Refresh the page.',
          );
        },
      });
  }

  protected loadStudentsPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getClassStudents();
  }

  protected loadSubjectsPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getClassStudents();
  }

  protected getDashboardData(): void {
    this.isLoading.set(true);

    this.dashboardService
      .getDashboardData(`${ApiEndpoints.url}/dashboard/class-enrollments/${this.classId}`)
      .subscribe({
        next: (res) => {
          this.dashboardData.set(res.payload);
          this.malePercentage.set(
            (this.dashboardData().maleStudents / this.dashboardData()?.totalStudents) * 100,
          );
          this.isLoading.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Failed to load dashboard. Refresh the page.',
          );
        },
      });
  }

  protected selectStudentEnrolled(action: string, payload: IStudentEnrollment['payload']): void {
    if (action === 'status') {
      this.changeStatus.set(true);
    } else {
      this.editEnrollment.set(true);
      this.startDate = new Date(payload.start_date).toISOString().split('T')[0];
      this.endDate = new Date(payload.end_date).toISOString().split('T')[0];
      this.enrollmentDate = new Date(payload.enrollment_date).toISOString().split('T')[0];
      this.currentEnrollment = payload.current_enrollment;
      this.getAcademicYear(payload.academic_year_id);
    }
    this.selectedStudent = payload;
  }

  protected getStudents(key: string): void {
    this.studentService.getStudents(`${ApiEndpoints.url}/student`, { search: key }).subscribe({
      next: (data: IStudents) => {
        this.students.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load students.');
      },
    });
  }

  protected selectStudent(student: IStudent['payload']) {
    this.query = '';
    this.studentId = student.id;
    this.studentPublicId = student.public_id;
    this.studentName = Helpers.fullName({
      first_name: student.first_name,
      last_name: student.last_name,
    });
  }

  protected navigateToStudentDetail(id: string): void {
    void this.router.navigate(['/dashboard/academic-management/student-details'], {
      queryParams: {
        id: id,
      },
    });
  }

  protected navigateToTakeAssessment(id: string, levelSubjectId: string): void {
    void this.router.navigate(['/dashboard/class/class-assessment'], {
      queryParams: {
        id: id,
        level_subject_id: levelSubjectId,
      },
    });
  }

  protected navigateToTeacherDetail(id: string): void {
    void this.router.navigate(['/dashboard/class/teacher-details'], {
      queryParams: {
        id: id,
      },
    });
  }

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 10,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Years. Refresh the page.',
          );
        },
      });
  }

  protected onAcademicYearChange(): void {
    const year = this.academicYears()?.payload.find((a) => a.id === this.academicYearId);

    if (year) {
      this.startDate = new Date(year.start_date).toISOString().split('T')[0];
      this.endDate = new Date(year.end_date).toISOString().split('T')[0];
    }

    this.getAcademicYear(this.academicYearId);
  }

  protected getAcademicYear(id: string): void {
    this.academicYearService
      .getAcademicYearById(`${ApiEndpoints.url}/academic-year/${id}`)
      .subscribe({
        next: (res) => {
          this.academicYear.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Year. Refresh the page.',
          );
        },
      });
  }

  protected addStudent(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error creating level. Try again.');
    }

    this.isSubmitting.set(true);

    const studentForm = {
      student_id: this.studentId,
      class_id: this.classId,
      academic_year_id: this.academicYearId,
      enrollment_date: this.enrollmentDate,
      start_date: this.startDate,
      end_date: this.endDate,
    };

    this.studentService
      .createStudent(`${ApiEndpoints.url}/student-enrollment`, studentForm)
      .subscribe({
        next: () => {
          this.getClassStudents();
          this.getDashboardData();
          this.isSubmitting.set(false);
          this.showAddStudent.set(false);
          this.enrollmentDate = '';
          this.startDate = '';
          this.endDate = '';
          this.flashService.success('Student enrolled to class successfully.');
          form.resetForm();
          this.studentPublicId = '';
          this.query = '';
          this.academicYearId = '';
          this.academicYear.set(null);
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error assigning student. Try again.');
        },
      });
  }

  protected changeStudentStatus(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    const editForm = {
      status: this.newStatus,
    };

    this.studentService
      .editClassStudent(
        `${ApiEndpoints.url}/student-enrollment/${this.selectedStudent.id}/edit`,
        editForm,
      )
      .subscribe({
        next: () => {
          this.getClassStudents();
          this.getDashboardData();
          this.isEditing.set(false);
          this.changeStatus.set(false);
          this.flashService.success("Successfully changed student's status");
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error changing status. Try again');
        },
      });
  }

  protected editStudentEnrollment(form: NgForm): void {
    if (form.invalid) return;

    const payload: any = {};

    if (this.enrollmentDate !== this.selectedStudent.enrollment_date) {
      payload.enrollment_date = this.enrollmentDate;
    }

    if (this.startDate !== this.selectedStudent.start_date) {
      payload.start_date = this.startDate;
    }

    if (this.endDate !== this.selectedStudent.end_date) {
      payload.end_date = this.endDate || null;
    }

    if (this.currentEnrollment !== this.selectedStudent.current_enrollment) {
      payload.current_enrollment = this.currentEnrollment;
    }

    if (Object.keys(payload).length === 0) {
      this.flashService.failure('No changes were made');
      return;
    }

    this.isSubmitting.set(true);

    this.studentService
      .editClassStudent(
        `${ApiEndpoints.url}/student-enrollment/${this.selectedStudent.id}/edit`,
        payload,
      )
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.editEnrollment.set(false);
          this.enrollmentDate = '';
          this.startDate = '';
          this.endDate = '';
          this.flashService.success('Student enrollment updated successfully.');
          this.getClassStudents();
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error updating enrollment details');
        },
      });
  }

  protected navigateToAttendancePage(): void {
    void this.router.navigate([`../class-attendance`], {
      relativeTo: this.route,
      queryParams: {
        id: this.classId,
      },
    });
  }

  protected openAssignClassTeacher(): void {
    this.selectedClassId = this.classId;
    this.assignClassTeacher.set(true);
  }

  protected selectTeacher(teacher: ITeacher['payload']) {
    this.query = '';
    this.teacherName = Helpers.fullName({
      first_name: teacher.first_name,
      last_name: teacher.last_name,
    });
    this.teacherPublicId = teacher.public_id;
    this.classTeacherId = teacher.id;
  }

  protected getTeachers(query: string): void {
    this.teacherService.getTeachers(`${ApiEndpoints.url}/teacher`, { search: query }).subscribe({
      next: (res) => {
        this.teachers.set(res);
        this.teacherName = '';
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load teachers. Refresh the page.',
        );
      },
    });
  }

  protected openAssignSubjectTeacher(): void {
    this.getLevelSubjects();
    this.assignSubjectTeacherCheck.set(true);
  }

  protected getLevelSubjects(): void {
    const levelId = this.class()?.payload?.level_id;

    if (!levelId) return;

    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
      level_id: levelId,
    };

    this.subjectService.getLevelSubjects(`${ApiEndpoints.url}/level-subject`, params).subscribe({
      next: (res) => {
        this.levelSubjects.set(res);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load subjects. Refresh the page',
        );
      },
    });
  }

  protected assignSubjectTeacher(form: NgForm): void {
    if (form.invalid && !this.classTeacherId) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error assigning Teacher. Try again.');
      return;
    }

    if (!this.selectedLevelSubject) {
      this.flashService.failure('Error assigning Teacher. Try again.');
      return;
    }

    this.isAssigningSubjectTeacher.set(true);

    const teacherSubjectForm = {
      school_id: this.schoolId,
      teacher_id: this.classTeacherId,
      level_subject_id: this.selectedLevelSubject.id,
      assignment_role: this.assignmentRole,
      class_id: this.classId,
    };

    this.teacherClassSubjectService
      .createTeacherClassSubject(`${ApiEndpoints.url}/teacher-class-subject`, teacherSubjectForm)
      .subscribe({
        next: () => {
          this.teacherName = '';
          this.teacherPublicId = '';
          this.classTeacherId = '';
          this.assignmentRole = '';
          this.isAssigningSubjectTeacher.set(false);
          this.flashService.success('Teacher assigned successfully.');
          this.assignSubjectTeacherCheck.set(false);
          this.getTeacherClassSubjects('', '', this.classId);
          this.getDashboardData();
        },
        error: (err) => {
          this.isAssigningSubjectTeacher.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error assigning Teacher. Try again.');
        },
      });
  }

  protected assignClassTeacherFn(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    const editForm = {
      class_teacher_id: this.classTeacherId,
    };

    this.classService
      .editClass(`${ApiEndpoints.url}/class/${this.selectedClassId}`, editForm)
      .subscribe({
        next: () => {
          this.teacherName = '';
          this.teacherPublicId = '';
          this.classTeacherId = '';
          this.getClass();
          this.getDashboardData();
          this.isEditing.set(false);
          this.selectedClassId = '';
          this.assignClassTeacher.set(false);
          this.flashService.success('Successfully assigned Class Teacher');
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Error assigning Class Teacher. Try again',
          );
        },
      });
  }
}
